'use client';

import { FC, useState } from 'react';
import { FaGithub } from 'react-icons/fa';
import { FiShare2, FiExternalLink, FiActivity, FiAward } from 'react-icons/fi';
import { format } from 'date-fns';
import Link from 'next/link';
import Image from 'next/image';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

interface ProjectCardProps {
  project: any;
}

const ProjectCard: FC<ProjectCardProps> = ({ project }) => {
  const [tooltipText, setTooltipText] = useState('Share this project');

  const handleShare = async () => {
    const projectUrl = `${process.env.NEXT_PUBLIC_SITE_URL ?? window.location.origin}/projects/${project.slug}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: project.title,
          text: project.excerpt,
          url: projectUrl,
        });
        setTooltipText('Shared!');
      } catch (err) {
        console.error(err);
        setTooltipText('Share failed');
      }
    } else {
      try {
        await navigator.clipboard.writeText(projectUrl);
        setTooltipText('Link copied!');
        toast.success('Project URL copied to clipboard!');
      } catch (err) {
        console.error(err);
        setTooltipText('Copy failed');
      }
    }

    // Reset tooltip text after 2 seconds
    setTimeout(() => setTooltipText('Share this project'), 2000);
  };

  const topMetrics = project.metrics?.slice(0, 2) || [];
  const remainingMetrics = project.metrics?.slice(2) || [];
  const imageSrc = project.coverImage ?? '/images/default-project.jpg';

  return (
    <article className="group relative flex flex-col h-full bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-[2.5rem] overflow-hidden transition-all duration-500 hover:border-indigo-500/50 hover:shadow-2xl hover:shadow-indigo-500/10">

      {/* Image Section */}
      <Link href={`/projects/${project.slug}`} className="block relative aspect-[16/10] overflow-hidden border-b border-zinc-100 dark:border-zinc-900">
        <Image
          src={imageSrc}
          alt={project.title}
          fill
          className="object-cover transition-transform duration-700 group-hover:scale-110"
          sizes="(max-width: 768px) 100vw, 33vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {project.category && (
          <div className="absolute top-5 left-5">
            <span className="bg-white/10 backdrop-blur-md text-white border border-white/20 px-4 py-1.5 rounded-2xl text-[9px] font-black uppercase tracking-[0.2em]">
              {project.category}
            </span>
          </div>
        )}
      </Link>

      {/* Content Section */}
      <div className="p-6 sm:p-8 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <time className="text-[9px] font-black text-zinc-400 dark:text-zinc-500 uppercase tracking-widest font-mono">
            {format(new Date(project.updatedAt || project.publishedAt), 'MM.yyyy')}
          </time>
          <div className="flex items-center gap-2 px-2 py-1 bg-emerald-500/10 rounded-lg">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[9px] font-black text-emerald-600 uppercase tracking-tighter">Verified</span>
          </div>
        </div>

        {/* Title */}
        <Link href={`/projects/${project.slug}`}>
          <h2
            className="text-xl sm:text-2xl font-black text-zinc-900 dark:text-white leading-tight mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors"
            title={project.title} // <-- Tooltip shows full title on hover
          >
            {project.title}
          </h2>
        </Link>

        {/* Metrics Row with Tooltip */}
        <div className="flex flex-wrap gap-1 mb-4">
          {topMetrics.map((m: any) => (
            <div
              key={m.id}
              className={clsx(
                "flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold uppercase truncate cursor-pointer",
                m.category === "PERFORMANCE" && "bg-emerald-100 text-emerald-700",
                m.category === "SCALE" && "bg-indigo-100 text-indigo-700",
                m.category === "IMPACT" && "bg-amber-100 text-amber-700",
                m.category === "ACADEMIC" && "bg-purple-100 text-purple-700",
                m.category === "COMPLEXITY" && "bg-red-100 text-red-700"
              )}
              data-tooltip-id={`metric-tooltip-${project.id}`}
              data-tooltip-content={m.displayValue || `${m.label}: ${m.value}${m.unit}`}
            >
              {m.category === 'ACADEMIC' ? (
                <FiAward className="text-purple-500" size={12} />
              ) : (
                <FiActivity className="text-indigo-500" size={12} />
              )}
              <span className="truncate max-w-[100px]">{m.displayValue || `${m.value}${m.unit}`}</span>
            </div>
          ))}

          {remainingMetrics.length > 0 && (
            <div
              className="flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold uppercase bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 cursor-pointer"
              data-tooltip-id={`metric-tooltip-${project.id}`}
              data-tooltip-content={remainingMetrics.map((m: any) => `${m.label}: ${m.value}${m.unit}`).join('\n')}
            >
              +{remainingMetrics.length} more
            </div>
          )}

          <Tooltip id={`metric-tooltip-${project.id}`} />
        </div>

        {/* Excerpt */}
        <p className="text-sm sm:text-[13px] text-zinc-700 dark:text-zinc-400 leading-relaxed mb-4 italic line-clamp-3">
          {project.excerpt}
        </p>

        {/* Tech Stack */}
        <div className="flex flex-wrap gap-2 mb-6">
          {project.techStack.slice(0, 6).map((tech: string) => (
            <span
              key={tech}
              className="px-3 py-1 text-[10px] font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded-full uppercase tracking-wider"
            >
              {tech}
            </span>
          ))}
        </div>

        {/* Action Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-zinc-100 dark:border-zinc-900 mt-auto">
          <div className="flex items-center gap-3">
            {project.repository && (
              <a
                href={project.repository}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View Repository"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-400 hover:text-white hover:bg-indigo-600 transition-all transform hover:-translate-y-1 shadow-sm"
              >
                <FaGithub size={18} />
              </a>
            )}
            {project.liveDemo && (
              <a
                href={project.liveDemo}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View Live Demo"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-400 hover:text-white hover:bg-indigo-600 transition-all transform hover:-translate-y-1 shadow-sm"
              >
                <FiExternalLink size={18} />
              </a>
            )}
          </div>

          {/* Share Button with Tooltip */}
          <button
            onClick={handleShare}
            aria-label={`Share project: ${project.title}`}
            data-tooltip-id={`share-tooltip-${project.id}`}
            data-tooltip-content={tooltipText}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-400 hover:text-white hover:bg-indigo-600 transition-all transform hover:-translate-y-1 shadow-sm"
          >
            <FiShare2 size={16} />
          </button>
          <Tooltip id={`share-tooltip-${project.id}`} />
        </div>
      </div>
    </article>
  );
};

export default ProjectCard;
